import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { FiArrowLeft, FiPlus, FiSave, FiUpload } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getApiUrl } from '../../config/api.config';
import { useAuth } from '../../contexts/AuthContext';

const FALLBACK_BRANDS = [
  'Maruti Suzuki', 'Hyundai', 'Tata Motors', 'Mahindra', 'Toyota', 'Kia', 'Honda',
];

const AddCar = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [brandCatalog, setBrandCatalog] = useState([]);
  const [brandsLoading, setBrandsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadBrands = async () => {
      setBrandsLoading(true);
      try {
        const response = await fetch(getApiUrl('/car-brands'));
        if (cancelled) return;
        if (response.ok) {
          const json = await response.json();
          setBrandCatalog(Array.isArray(json?.data) ? json.data : []);
        }
      } catch (error) {
        console.warn('Failed to load brand catalog', error?.message || error);
      } finally {
        if (!cancelled) setBrandsLoading(false);
      }
    };
    loadBrands();
    return () => {
      cancelled = true;
    };
  }, []);
  const [formData, setFormData] = useState({
    brand: '',
    model: '',
    year: '',
    price: '',
    fuelType: '',
    transmission: '',
    bodyType: '',
    kmDriven: '',
    mileage: '',
    location: '',
    description: '',
    seller: '',
    color: '',
    customColor: '',
    features: [],
    // Private Details
    privateDetails: {
      ownerName: '',
      ownerPhone: '',
      ownerEmail: '',
      ownerAddress: '',
      insuranceProvider: '',
      insurancePolicyNumber: '',
      insuranceExpiryDate: '',
      rcNumber: '',
      chassisNumber: '',
      engineNumber: '',
      bankFinanced: false,
      bankName: '',
      loanOutstanding: '',
      noc: false,
      lastServiceDate: '',
      serviceCenter: '',
      additionalNotes: '',
      acquisitionDate: '',
      purchasePrice: ''
    }
  });

  const [images, setImages] = useState([]);
  const [imagePreview, setImagePreview] = useState([]);
  const [privateDocuments, setPrivateDocuments] = useState([]);
  const [privateDocPreview, setPrivateDocPreview] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customFeature, setCustomFeature] = useState('');
  const [showCustomFeatureInput, setShowCustomFeatureInput] = useState(false);

  const brands = useMemo(() => {
    if (brandCatalog.length === 0) return FALLBACK_BRANDS;
    return brandCatalog.map((entry) => entry.name);
  }, [brandCatalog]);

  const modelOptionsForSelectedBrand = useMemo(() => {
    if (!formData.brand || brandCatalog.length === 0) return [];
    const match = brandCatalog.find((entry) => entry.name === formData.brand);
    return match ? (match.models || []).map((m) => m.name) : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.brand, brandCatalog]);

  const fuelTypes = ['Petrol', 'Diesel', 'CNG', 'Electric', 'Hybrid'];
  const transmissionTypes = ['Manual', 'Automatic', 'CVT', 'AMT'];
  const bodyTypes = ['Hatchback', 'Sedan', 'SUV', 'MUV', 'Coupe', 'Convertible', 'Pickup Truck', 'Van'];
  const locationOptions = [
    'Adoni',
    'Amalapuram',
    'Anakapalle',
    'Anantapur',
    'Atmakur',
    'Bapatla',
    'Bhimavaram',
    'Bobbili',
    'Chirala',
    'Chittoor',
    'Cuddapah (Kadapa)',
    'Dharmavaram',
    'Dhone',
    'Eluru',
    'Gajuwaka',
    'Gooty',
    'Gudivada',
    'Gudur',
    'Guntur',
    'Hindupur',
    'Jaggaiahpet',
    'Jammalamadugu',
    'Kadapa',
    'Kakinada',
    'Kandukur',
    'Kavali',
    'Kurnool',
    'Machilipatnam',
    'Madanapalle',
    'Nandyal',
    'Narasapuram',
    'Narasaraopet',
    'Nellore',
    'Ongole',
    'Palakollu',
    'Palasa',
    'Peddapuram',
    'Proddatur',
    'Rajahmundry',
    'Rayachoti',
    'Srikakulam',
    'Sattenapalle',
    'Tadipatri',
    'Tadepalligudem',
    'Tenali',
    'Tirupati',
    'Vijayawada',
    'Visakhapatnam',
    'Vizianagaram',
  ];

  const carColors = [
    { name: 'White', hex: '#FFFFFF', border: true },
    { name: 'Black', hex: '#000000' },
    { name: 'Silver', hex: '#C0C0C0' },
    { name: 'Grey', hex: '#808080' },
    { name: 'Red', hex: '#DC2626' },
    { name: 'Blue', hex: '#2563EB' },
    { name: 'Dark Blue', hex: '#1E3A8A' },
    { name: 'Green', hex: '#16A34A' },
    { name: 'Yellow', hex: '#EAB308' },
    { name: 'Orange', hex: '#EA580C' },
    { name: 'Brown', hex: '#92400E' },
    { name: 'Beige', hex: '#D4A574' },
    { name: 'Maroon', hex: '#7F1D1D' },
    { name: 'Gold', hex: '#D97706' },
    { name: 'Pearl White', hex: '#F5F5F5', border: true },
    { name: 'Other', hex: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', isOther: true }
  ];

  const features = [
    'Air Conditioning', 'Power Steering', 'ABS', 'Airbags', 'Central Locking',
    'Power Windows', 'Music System', 'Bluetooth', 'GPS Navigation', 'Sunroof',
    'Leather Seats', 'Alloy Wheels', 'Fog Lights', 'Rear Parking Sensor'
  ];

  const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
  const MAX_PUBLIC_IMAGES = 5;
  const MAX_PRIVATE_DOCUMENTS = 5;
  const MAX_TOTAL_UPLOAD_BYTES = 50 * 1024 * 1024;

  const formatSizeMB = (bytes) => `${(bytes / (1024 * 1024)).toFixed(2)}MB`;

  const splitFilesBySize = (files = []) => {
    const valid = [];
    const invalid = [];

    files.forEach((file) => {
      if (Number(file?.size) > 0 && file.size <= MAX_FILE_SIZE_BYTES) {
        valid.push(file);
      } else {
        invalid.push(file);
      }
    });

    return { valid, invalid };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePrivateDetailsChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      privateDetails: {
        ...prev.privateDetails,
        [name]: type === 'checkbox' ? checked : value
      }
    }));
  };

  const handleColorSelect = (colorName) => {
    setFormData(prev => ({
      ...prev,
      color: colorName,
      customColor: colorName === 'Other' ? prev.customColor : ''
    }));
  };

  const handleCustomColorChange = (e) => {
    setFormData(prev => ({
      ...prev,
      customColor: e.target.value
    }));
  };

  const handleFeatureToggle = (feature) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature]
    }));
  };

  const handleAddCustomFeature = () => {
    if (customFeature.trim() && !formData.features.includes(customFeature.trim())) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, customFeature.trim()]
      }));
      setCustomFeature('');
      setShowCustomFeatureInput(false);
      toast.success(`Added custom feature: ${customFeature.trim()}`);
    } else if (formData.features.includes(customFeature.trim())) {
      toast.error('This feature already exists');
    }
  };

  const handleRemoveCustomFeature = (feature) => {
    // Only allow removing custom features (not predefined ones)
    const predefinedFeatures = [
      'Air Conditioning', 'Power Steering', 'ABS', 'Airbags', 'Central Locking',
      'Power Windows', 'Music System', 'Bluetooth', 'GPS Navigation', 'Sunroof',
      'Leather Seats', 'Alloy Wheels', 'Fog Lights', 'Rear Parking Sensor'
    ];
    
    if (!predefinedFeatures.includes(feature)) {
      setFormData(prev => ({
        ...prev,
        features: prev.features.filter(f => f !== feature)
      }));
      toast.info(`Removed custom feature: ${feature}`);
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const { valid, invalid } = splitFilesBySize(files);
    if (invalid.length > 0) {
      const invalidNames = invalid.map((file) => `${file.name} (${formatSizeMB(file.size || 0)})`).join(', ');
      toast.error(`Skipped oversized public image(s) over 5MB: ${invalidNames}`);
    }

    if (!valid.length) {
      e.target.value = '';
      return;
    }

    const remainingSlots = MAX_PUBLIC_IMAGES - images.length;
    if (remainingSlots <= 0) {
      toast.error(`You can upload up to ${MAX_PUBLIC_IMAGES} public images.`);
      e.target.value = '';
      return;
    }

    const acceptedByCount = valid.slice(0, remainingSlots);
    if (acceptedByCount.length < valid.length) {
      toast.error(`Only ${remainingSlots} more public image(s) can be added.`);
    }

    const currentTotalBytes = [...images, ...privateDocuments].reduce((sum, file) => sum + (Number(file?.size) || 0), 0);
    const accepted = [];
    let projected = currentTotalBytes;

    acceptedByCount.forEach((file) => {
      const size = Number(file?.size) || 0;
      if (projected + size <= MAX_TOTAL_UPLOAD_BYTES) {
        accepted.push(file);
        projected += size;
      }
    });

    if (!accepted.length) {
      toast.error('Adding these files would exceed the 50MB total upload limit.');
      e.target.value = '';
      return;
    }

    if (accepted.length < acceptedByCount.length) {
      toast.error('Some files were skipped to keep total upload size under 50MB.');
    }

    setImages(prev => [...prev, ...accepted]);
    const previews = accepted.map((file) => URL.createObjectURL(file));
    setImagePreview(prev => [...prev, ...previews]);
    e.target.value = '';
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setImagePreview(prev => prev.filter((_, i) => i !== index));
  };

  const handlePrivateDocUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const { valid, invalid } = splitFilesBySize(files);
    if (invalid.length > 0) {
      const invalidNames = invalid.map((file) => `${file.name} (${formatSizeMB(file.size || 0)})`).join(', ');
      toast.error(`Skipped oversized private document(s) over 5MB: ${invalidNames}`);
    }

    if (!valid.length) {
      e.target.value = '';
      return;
    }

    const remainingSlots = MAX_PRIVATE_DOCUMENTS - privateDocuments.length;
    if (remainingSlots <= 0) {
      toast.error(`You can upload up to ${MAX_PRIVATE_DOCUMENTS} private documents.`);
      e.target.value = '';
      return;
    }

    const acceptedByCount = valid.slice(0, remainingSlots);
    if (acceptedByCount.length < valid.length) {
      toast.error(`Only ${remainingSlots} more private document(s) can be added.`);
    }

    const currentTotalBytes = [...images, ...privateDocuments].reduce((sum, file) => sum + (Number(file?.size) || 0), 0);
    const accepted = [];
    let projected = currentTotalBytes;

    acceptedByCount.forEach((file) => {
      const size = Number(file?.size) || 0;
      if (projected + size <= MAX_TOTAL_UPLOAD_BYTES) {
        accepted.push(file);
        projected += size;
      }
    });

    if (!accepted.length) {
      toast.error('Adding these files would exceed the 50MB total upload limit.');
      e.target.value = '';
      return;
    }

    if (accepted.length < acceptedByCount.length) {
      toast.error('Some files were skipped to keep total upload size under 50MB.');
    }

    setPrivateDocuments(prev => [...prev, ...accepted]);

    const previews = accepted.map((file) => ({
      url: URL.createObjectURL(file),
      name: file.name,
      type: file.type,
    }));
    setPrivateDocPreview(prev => [...prev, ...previews]);
    e.target.value = '';
  };

  const removePrivateDoc = (index) => {
    setPrivateDocuments(prev => prev.filter((_, i) => i !== index));
    setPrivateDocPreview(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.brand || !formData.model || !formData.year || !formData.price || !formData.bodyType || !formData.location) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      // Get auth token - use the correct key from TokenManager
      const token = localStorage.getItem('agent_token');
      if (!token) {
        toast.error('Please login first. Redirecting to login page...');
        setIsSubmitting(false);
        setTimeout(() => navigate('/signin'), 1500);
        return;
      }

      // Show logged in user in console
      console.log('Logged in as:', user?.name || 'Unknown User', user?.email);

      // Defensive validation before submit
      const allUploads = [...images, ...privateDocuments];
      for (const file of allUploads) {
        if (!Number(file?.size) || file.size > MAX_FILE_SIZE_BYTES) {
          toast.error(`File "${file.name}" is invalid. Each file must be under 5MB.`);
          setIsSubmitting(false);
          return;
        }
      }

      if (images.length > MAX_PUBLIC_IMAGES) {
        toast.error(`You can upload up to ${MAX_PUBLIC_IMAGES} public images.`);
        setIsSubmitting(false);
        return;
      }

      if (privateDocuments.length > MAX_PRIVATE_DOCUMENTS) {
        toast.error(`You can upload up to ${MAX_PRIVATE_DOCUMENTS} private documents.`);
        setIsSubmitting(false);
        return;
      }

      const totalUploadBytes = allUploads.reduce((sum, file) => sum + (Number(file?.size) || 0), 0);
      if (totalUploadBytes > MAX_TOTAL_UPLOAD_BYTES) {
        toast.error('Total upload size cannot exceed 50MB.');
        setIsSubmitting(false);
        return;
      }

      const carData = {
        title: `${formData.brand} ${formData.model}`,
        brand: formData.brand,
        model: formData.model,
        year: parseInt(formData.year),
        price: parseFloat(formData.price),
        fuelType: formData.fuelType,
        transmission: formData.transmission,
        bodyType: formData.bodyType,
        kmDriven: parseInt(formData.kmDriven) || 0,
        mileage: parseFloat(formData.mileage) || 0,
        color: formData.color === 'Other' ? formData.customColor : formData.color,
        ownerType: 'First Owner', // Default value
        location: {
          city: formData.location.split(',')[0]?.trim() || 'Unknown',
          state: formData.location.split(',')[1]?.trim() || 'Unknown'
        },
        description: formData.description,
        features: formData.features,
        negotiable: true,
        condition: 'Good',
        accidentHistory: false,
        floodAffected: false,
        documents: {
          rcAvailable: true,
          insuranceValid: true,
          pucValid: true,
          serviceHistory: false
        },
        testDriveAvailable: true,
        // Include private details
        privateDetails: {
          ownerName: formData.privateDetails.ownerName || undefined,
          ownerPhone: formData.privateDetails.ownerPhone || undefined,
          ownerEmail: formData.privateDetails.ownerEmail || undefined,
          ownerAddress: formData.privateDetails.ownerAddress || undefined,
          insuranceProvider: formData.privateDetails.insuranceProvider || undefined,
          insurancePolicyNumber: formData.privateDetails.insurancePolicyNumber || undefined,
          insuranceExpiryDate: formData.privateDetails.insuranceExpiryDate || undefined,
          rcNumber: formData.privateDetails.rcNumber || undefined,
          chassisNumber: formData.privateDetails.chassisNumber || undefined,
          engineNumber: formData.privateDetails.engineNumber || undefined,
          bankFinanced: formData.privateDetails.bankFinanced,
          bankName: formData.privateDetails.bankName || undefined,
          loanOutstanding: formData.privateDetails.loanOutstanding ? parseFloat(formData.privateDetails.loanOutstanding) : undefined,
          noc: formData.privateDetails.noc,
          lastServiceDate: formData.privateDetails.lastServiceDate || undefined,
          serviceCenter: formData.privateDetails.serviceCenter || undefined,
          additionalNotes: formData.privateDetails.additionalNotes || undefined,
          acquisitionDate: formData.privateDetails.acquisitionDate || undefined,
          purchasePrice: formData.privateDetails.purchasePrice ? parseFloat(formData.privateDetails.purchasePrice) : undefined
        }
      };

      const multipartData = new FormData();
      Object.entries(carData).forEach(([key, value]) => {
        if (value === undefined || value === null) return;

        if (typeof value === 'object') {
          multipartData.append(key, JSON.stringify(value));
        } else {
          multipartData.append(key, String(value));
        }
      });

      images.forEach((file) => {
        multipartData.append('images', file);
      });

      privateDocuments.forEach((file) => {
        multipartData.append('privateDocuments', file);
      });

      // Send to backend
      const response = await fetch(getApiUrl('/cars'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: multipartData
      });

      // Handle non-JSON responses
      const contentType = response.headers.get('content-type');
      let result;
      
      if (contentType && contentType.includes('application/json')) {
        result = await response.json();
      } else {
        const text = await response.text();
        console.error('Non-JSON response:', text.substring(0, 200)); // Only log first 200 chars
        throw new Error('Server returned invalid response');
      }
      
      console.log('Backend response:', result);

      if (response.ok && result.success) {
        toast.success('Car added successfully!');
        
        // Reset form
        setFormData({
          brand: '',
          model: '',
          year: '',
          price: '',
          fuelType: '',
          transmission: '',
          bodyType: '',
          kmDriven: '',
          mileage: '',
          location: '',
          description: '',
          seller: '',
          color: '',
          customColor: '',
          features: [],
          privateDetails: {
            ownerName: '',
            ownerPhone: '',
            ownerEmail: '',
            ownerAddress: '',
            insuranceProvider: '',
            insurancePolicyNumber: '',
            insuranceExpiryDate: '',
            rcNumber: '',
            chassisNumber: '',
            engineNumber: '',
            bankFinanced: false,
            bankName: '',
            loanOutstanding: '',
            noc: false,
            lastServiceDate: '',
            serviceCenter: '',
            additionalNotes: '',
            acquisitionDate: '',
            purchasePrice: ''
          }
        });
        setImages([]);
        setImagePreview([]);
        setPrivateDocuments([]);
        setPrivateDocPreview([]);

        // Navigate back to car management
        setTimeout(() => {
          navigate('/dashboard/cars');
        }, 2000);
      } else {
        const errorMsg = result.message || 'Failed to add car';
        console.error('Server error:', errorMsg);
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Error adding car:', error);
      
      // Better error handling
      if (error.message.includes('Failed to fetch')) {
        toast.error('Network error. Please check your connection and try again.');
      } else if (error.name === 'TypeError') {
        toast.error('Invalid data format. Please check all fields.');
      } else {
        toast.error('Failed to add car. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col space-y-4"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/dashboard/cars">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200"
              >
                <FiArrowLeft className="h-5 w-5" />
              </motion.button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Add New Car</h1>
              <p className="mt-2 text-gray-600">Add a new car to your inventory</p>
            </div>
          </div>
        </div>
        
        {/* Logged in user info */}
        {user && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-200 rounded-lg p-4"
          >
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 rounded-full gradient-bg flex items-center justify-center text-white font-semibold">
                {user.name?.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Logged in as: <span className="font-bold text-orange-600">{user.name}</span>
                </p>
                <p className="text-xs text-gray-600">{user.email}</p>
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-2xl p-8 card-shadow"
      >
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Basic Information</h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Brand *{brandsLoading ? <span className="text-xs text-gray-400 ml-2">(loading...)</span> : null}
                </label>
                <select
                  name="brand"
                  value={formData.brand}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      brand: e.target.value,
                      model: '',
                    }))
                  }
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">Select Brand</option>
                  {brands.map(brand => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Model *
                </label>
                <select
                  name="model"
                  value={formData.model}
                  onChange={handleInputChange}
                  required
                  disabled={!formData.brand || modelOptionsForSelectedBrand.length === 0}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                >
                  <option value="">
                    {!formData.brand
                      ? 'Pick a brand first'
                      : modelOptionsForSelectedBrand.length === 0
                      ? `No models configured for ${formData.brand}`
                      : 'Select Model'}
                  </option>
                  {modelOptionsForSelectedBrand.map((modelName) => (
                    <option key={modelName} value={modelName}>{modelName}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Year *
                </label>
                <input
                  type="number"
                  name="year"
                  value={formData.year}
                  onChange={handleInputChange}
                  required
                  min="1990"
                  max={new Date().getFullYear()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Enter year"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price (₹) *
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  required
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Enter price"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fuel Type *
                </label>
                <select
                  name="fuelType"
                  value={formData.fuelType}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">Select Fuel Type</option>
                  {fuelTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transmission *
                </label>
                <select
                  name="transmission"
                  value={formData.transmission}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">Select Transmission</option>
                  {transmissionTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Body Type *
                </label>
                <select
                  name="bodyType"
                  value={formData.bodyType}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">Select Body Type</option>
                  {bodyTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Color Palette Selector */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Color *
              </label>
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-5 lg:grid-cols-7">
                {carColors.map((color) => (
                  <motion.button
                    key={color.name}
                    type="button"
                    onClick={() => handleColorSelect(color.name)}
                    whileHover={{ scale: 1.08 }}
                    whileTap={{ scale: 0.95 }}
                    className={`relative flex flex-col items-center p-3 rounded-xl transition-all duration-200 ${
                      formData.color === color.name
                        ? 'bg-orange-50 border-2 border-orange-500 shadow-lg'
                        : 'bg-gray-50 border-2 border-gray-200 hover:border-orange-300'
                    }`}
                  >
                    {color.isOther ? (
                      <div className="w-12 h-12 rounded-full shadow-md" style={{ background: color.hex }}>
                        <div className="w-full h-full rounded-full flex items-center justify-center text-white font-bold text-xs">
                          +
                        </div>
                      </div>
                    ) : (
                      <div
                        className={`w-12 h-12 rounded-full shadow-md ${color.border ? 'border-2 border-gray-300' : ''}`}
                        style={{ backgroundColor: color.hex }}
                      />
                    )}
                    <span className={`mt-2 text-xs font-medium text-center ${
                      formData.color === color.name ? 'text-orange-600' : 'text-gray-700'
                    }`}>
                      {color.name}
                    </span>
                    {formData.color === color.name && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center"
                      >
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </motion.div>
                    )}
                  </motion.button>
                ))}
              </div>
              
              {/* Custom Color Input - Shows when "Other" is selected */}
              {formData.color === 'Other' && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-4"
                >
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enter Custom Color Name *
                  </label>
                  <input
                    type="text"
                    value={formData.customColor}
                    onChange={handleCustomColorChange}
                    required={formData.color === 'Other'}
                    placeholder="e.g., Metallic Blue, Champagne Gold, etc."
                    className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </motion.div>
              )}
              
              {formData.color && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-3 text-sm text-gray-600"
                >
                  Selected color: <span className="font-semibold text-orange-600">
                    {formData.color === 'Other' && formData.customColor ? formData.customColor : formData.color}
                  </span>
                </motion.p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 mt-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  KM Driven *
                </label>
                <input
                  type="number"
                  name="kmDriven"
                  value={formData.kmDriven}
                  onChange={handleInputChange}
                  required
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Enter kilometers driven"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mileage (km/l)
                </label>
                <input
                  type="number"
                  name="mileage"
                  value={formData.mileage}
                  onChange={handleInputChange}
                  min="0"
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="e.g., 18.5"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location
                </label>
                <select
                  value={formData.location}
                  onChange={(e) => setFormData((prev) => ({ ...prev, location: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">Select Location</option>
                  {locationOptions.map((locationOption) => (
                    <option key={locationOption} value={locationOption}>{locationOption}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Seller
                </label>
                <input
                  type="text"
                  name="seller"
                  value={formData.seller}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Enter seller name"
                />
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="Enter car description..."
            />
          </div>

          {/* Features */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Features</h3>
              <motion.button
                type="button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowCustomFeatureInput(!showCustomFeatureInput)}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-orange-600 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors"
              >
                <FiPlus className="mr-1 h-4 w-4" />
                Add Custom Feature
              </motion.button>
            </div>

            {/* Custom Feature Input */}
            {showCustomFeatureInput && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-4 p-4 bg-orange-50 rounded-lg border border-orange-200"
              >
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customFeature}
                    onChange={(e) => setCustomFeature(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddCustomFeature();
                      }
                    }}
                    placeholder="Enter custom feature name..."
                    className="flex-1 px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleAddCustomFeature}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                  >
                    Add
                  </motion.button>
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setShowCustomFeatureInput(false);
                      setCustomFeature('');
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </motion.button>
                </div>
              </motion.div>
            )}

            {/* Predefined Features */}
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {features.map(feature => (
                <motion.label
                  key={feature}
                  whileHover={{ scale: 1.02 }}
                  className="flex items-center space-x-3 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={formData.features.includes(feature)}
                    onChange={() => handleFeatureToggle(feature)}
                    className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                  <span className="text-sm text-gray-700">{feature}</span>
                </motion.label>
              ))}
            </div>

            {/* Custom Features Display */}
            {formData.features.filter(f => !features.includes(f)).length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Custom Features:</p>
                <div className="flex flex-wrap gap-2">
                  {formData.features
                    .filter(f => !features.includes(f))
                    .map(feature => (
                      <motion.div
                        key={feature}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="inline-flex items-center px-3 py-1.5 bg-orange-100 text-orange-800 rounded-full text-sm"
                      >
                        <span>{feature}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveCustomFeature(feature)}
                          className="ml-2 text-orange-600 hover:text-orange-800"
                        >
                          ×
                        </button>
                      </motion.div>
                    ))}
                </div>
              </div>
            )}
          </div>

          {/* Private Details Section */}
          <div>
            <div className="mb-4">
              <div className="flex items-center space-x-2 mb-2">
                <h3 className="text-lg font-semibold text-gray-900">Private Details</h3>
                <span className="px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-full">
                  🔒 Confidential - Not shown to buyers
                </span>
              </div>
              <p className="text-sm text-gray-600">
                This information is only visible to agents and admins. It will never be shown to buyers or on the public website.
              </p>
            </div>

            {/* Owner Details */}
            <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-6 border-2 border-red-200 mb-6">
              <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">👤</span> Owner Information
              </h4>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Owner Name
                  </label>
                  <input
                    type="text"
                    name="ownerName"
                    value={formData.privateDetails.ownerName}
                    onChange={handlePrivateDetailsChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Enter owner name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Owner Phone
                  </label>
                  <input
                    type="tel"
                    name="ownerPhone"
                    value={formData.privateDetails.ownerPhone}
                    onChange={handlePrivateDetailsChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Enter phone number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Owner Email
                  </label>
                  <input
                    type="email"
                    name="ownerEmail"
                    value={formData.privateDetails.ownerEmail}
                    onChange={handlePrivateDetailsChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Enter email address"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Owner Address
                  </label>
                  <input
                    type="text"
                    name="ownerAddress"
                    value={formData.privateDetails.ownerAddress}
                    onChange={handlePrivateDetailsChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Enter complete address"
                  />
                </div>
              </div>
            </div>

            {/* Insurance Details */}
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-6 border-2 border-blue-200 mb-6">
              <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">🛡️</span> Insurance Details
              </h4>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Insurance Provider
                  </label>
                  <input
                    type="text"
                    name="insuranceProvider"
                    value={formData.privateDetails.insuranceProvider}
                    onChange={handlePrivateDetailsChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="e.g., ICICI Lombard"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Policy Number
                  </label>
                  <input
                    type="text"
                    name="insurancePolicyNumber"
                    value={formData.privateDetails.insurancePolicyNumber}
                    onChange={handlePrivateDetailsChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Enter policy number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Insurance Expiry Date
                  </label>
                  <input
                    type="date"
                    name="insuranceExpiryDate"
                    value={formData.privateDetails.insuranceExpiryDate}
                    onChange={handlePrivateDetailsChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
              </div>
            </div>

            {/* Vehicle Documents */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border-2 border-green-200 mb-6">
              <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">📄</span> Vehicle Documents
              </h4>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    RC Number
                  </label>
                  <input
                    type="text"
                    name="rcNumber"
                    value={formData.privateDetails.rcNumber}
                    onChange={handlePrivateDetailsChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Enter RC number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Chassis Number
                  </label>
                  <input
                    type="text"
                    name="chassisNumber"
                    value={formData.privateDetails.chassisNumber}
                    onChange={handlePrivateDetailsChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Enter chassis number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Engine Number
                  </label>
                  <input
                    type="text"
                    name="engineNumber"
                    value={formData.privateDetails.engineNumber}
                    onChange={handlePrivateDetailsChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Enter engine number"
                  />
                </div>
              </div>
            </div>

            {/* Finance Details */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border-2 border-purple-200 mb-6">
              <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">🏦</span> Finance Details
              </h4>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    name="bankFinanced"
                    checked={formData.privateDetails.bankFinanced}
                    onChange={handlePrivateDetailsChange}
                    className="rounded border-gray-300 text-orange-600 focus:ring-orange-500 h-5 w-5"
                  />
                  <label className="text-sm font-medium text-gray-700">
                    Bank Financed
                  </label>
                </div>
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    name="noc"
                    checked={formData.privateDetails.noc}
                    onChange={handlePrivateDetailsChange}
                    className="rounded border-gray-300 text-orange-600 focus:ring-orange-500 h-5 w-5"
                  />
                  <label className="text-sm font-medium text-gray-700">
                    NOC Available
                  </label>
                </div>
              </div>
              {formData.privateDetails.bankFinanced && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="grid grid-cols-1 gap-4 sm:grid-cols-2 mt-4"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bank Name
                    </label>
                    <input
                      type="text"
                      name="bankName"
                      value={formData.privateDetails.bankName}
                      onChange={handlePrivateDetailsChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="Enter bank name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Loan Outstanding (₹)
                    </label>
                    <input
                      type="number"
                      name="loanOutstanding"
                      value={formData.privateDetails.loanOutstanding}
                      onChange={handlePrivateDetailsChange}
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="Enter outstanding amount"
                    />
                  </div>
                </motion.div>
              )}
            </div>

            {/* Service & Purchase History */}
            <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-6 border-2 border-yellow-200 mb-6">
              <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">🔧</span> Service & Purchase History
              </h4>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Service Date
                  </label>
                  <input
                    type="date"
                    name="lastServiceDate"
                    value={formData.privateDetails.lastServiceDate}
                    onChange={handlePrivateDetailsChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service Center
                  </label>
                  <input
                    type="text"
                    name="serviceCenter"
                    value={formData.privateDetails.serviceCenter}
                    onChange={handlePrivateDetailsChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Enter service center name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Acquisition Date
                  </label>
                  <input
                    type="date"
                    name="acquisitionDate"
                    value={formData.privateDetails.acquisitionDate}
                    onChange={handlePrivateDetailsChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Purchase Price (₹)
                  </label>
                  <input
                    type="number"
                    name="purchasePrice"
                    value={formData.privateDetails.purchasePrice}
                    onChange={handlePrivateDetailsChange}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Enter purchase price"
                  />
                </div>
              </div>
            </div>

            {/* Additional Notes */}
            <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl p-6 border-2 border-gray-200">
              <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">📝</span> Additional Private Notes
              </h4>
              <textarea
                name="additionalNotes"
                value={formData.privateDetails.additionalNotes}
                onChange={handlePrivateDetailsChange}
                rows={4}
                maxLength={1000}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="Enter any additional private notes or observations about the car, owner, or transaction..."
              />
              <p className="mt-2 text-xs text-gray-500">
                {formData.privateDetails.additionalNotes.length}/1000 characters
              </p>
            </div>
          </div>

          {/* Public Images Upload */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <h3 className="text-lg font-semibold text-gray-900">Public Images</h3>
                <span className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 border border-green-300 rounded-full flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Visible to Buyers
                </span>
              </div>
              {images.length > 0 && (
                <span className="px-3 py-1 text-sm font-medium text-green-600 bg-green-50 rounded-full">
                  {images.length}/{MAX_PUBLIC_IMAGES} {images.length === 1 ? 'image' : 'images'} uploaded
                </span>
              )}
            </div>
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6 space-y-4">
              <div className="flex items-start space-x-2 mb-4">
                <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Public Images Warning:</span> These images will be visible to all buyers on the website and app. Upload high-quality photos showing exterior, interior, and key features.
                </p>
              </div>
              <div className="flex items-center justify-center w-full">
                <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-green-300 border-dashed rounded-lg cursor-pointer bg-white hover:bg-green-50 transition-colors">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <FiUpload className="w-8 h-8 mb-4 text-green-600" />
                    <p className="mb-2 text-sm text-gray-700">
                      <span className="font-semibold">Click to upload public images</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-600">PNG, JPG or JPEG (MAX. 5MB each, up to 5 files)</p>
                  </div>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </label>
              </div>

              {/* Image Preview */}
              {imagePreview.length > 0 && (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                  {imagePreview.map((image, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="relative group"
                    >
                      <img
                        src={image}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg border-2 border-green-200"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ×
                      </button>
                      <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-green-600 text-white text-xs rounded-full">
                        Public
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Private Documents Upload */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <h3 className="text-lg font-semibold text-gray-900">Private Documents</h3>
                <span className="px-2 py-1 text-xs font-medium text-red-700 bg-red-100 border border-red-300 rounded-full flex items-center">
                  🔒 Confidential - Agents Only
                </span>
              </div>
              {privateDocuments.length > 0 && (
                <span className="px-3 py-1 text-sm font-medium text-red-600 bg-red-50 rounded-full">
                  {privateDocuments.length}/{MAX_PRIVATE_DOCUMENTS} {privateDocuments.length === 1 ? 'document' : 'documents'} uploaded
                </span>
              )}
            </div>
            <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-200 rounded-xl p-6 space-y-4">
              <div className="flex items-start space-x-2 mb-4">
                <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Private Documents:</span> Upload confidential documents like RC copy, insurance papers, NOC, service records, etc. These will <span className="font-bold text-red-600">NEVER be visible to buyers</span> - only agents and admins can access them.
                </p>
              </div>
              <div className="flex items-center justify-center w-full">
                <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-red-300 border-dashed rounded-lg cursor-pointer bg-white hover:bg-red-50 transition-colors">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <FiUpload className="w-8 h-8 mb-4 text-red-600" />
                    <p className="mb-2 text-sm text-gray-700">
                      <span className="font-semibold">Click to upload private documents</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-600">PDF, PNG, JPG, JPEG (MAX. 5MB each, up to 5 files)</p>
                    <p className="text-xs text-red-600 font-semibold mt-2">🔒 100% Confidential - Never shown to buyers</p>
                  </div>
                  <input
                    type="file"
                    multiple
                    accept="image/*,application/pdf"
                    onChange={handlePrivateDocUpload}
                    className="hidden"
                  />
                </label>
              </div>

              {/* Private Document Preview */}
              {privateDocPreview.length > 0 && (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                  {privateDocPreview.map((doc, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="relative group bg-white rounded-lg border-2 border-red-200 p-3"
                    >
                      {doc.type.startsWith('image/') ? (
                        <img
                          src={doc.url}
                          alt={doc.name}
                          className="w-full h-32 object-cover rounded"
                        />
                      ) : (
                        <div className="w-full h-32 flex flex-col items-center justify-center bg-red-50 rounded">
                          <svg className="w-12 h-12 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                          </svg>
                          <p className="text-xs text-gray-600 mt-2 text-center">PDF Document</p>
                        </div>
                      )}
                      <p className="text-xs text-gray-600 mt-2 truncate" title={doc.name}>
                        {doc.name}
                      </p>
                      <button
                        type="button"
                        onClick={() => removePrivateDoc(index)}
                        className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ×
                      </button>
                      <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-red-600 text-white text-xs rounded-full flex items-center">
                        🔒 Private
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t">
            <Link to="/dashboard/cars">
              <motion.button
                type="button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-all duration-200"
              >
                Cancel
              </motion.button>
            </Link>
            <motion.button
              type="submit"
              disabled={isSubmitting}
              whileHover={{ scale: isSubmitting ? 1 : 1.05 }}
              whileTap={{ scale: isSubmitting ? 1 : 0.95 }}
              className={`inline-flex items-center px-8 py-3 ${isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'gradient-bg hover:shadow-xl'} text-white font-medium rounded-xl shadow-lg transition-all duration-200`}
            >
              <FiSave className="mr-2 h-5 w-5" />
              {isSubmitting ? 'Adding Car...' : 'Add Car'}
            </motion.button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default AddCar;